import { useEffect, useRef, type CSSProperties } from "react";
import { useExperience } from "../hooks/useExperience";

export default function HeroWord() {
  const word = useRef<HTMLSpanElement>(null);
  const { reducedMotion, paused, staticMode } = useExperience();

  useEffect(() => {
    const element = word.current;
    const hero = element?.closest<HTMLElement>(".lab-hero");
    if (!element || !hero || reducedMotion || paused || staticMode) return;

    const preference = matchMedia(
      "(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)",
    );
    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const reset = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      element.style.removeProperty("--move-pitch");
      element.style.removeProperty("--move-yaw");
    };
    const update = () => {
      frame = 0;
      const bounds = element.getBoundingClientRect();
      const clamp = (value: number) => Math.max(-1, Math.min(1, value));
      const x = clamp((pointerX - bounds.left - bounds.width / 2) / Math.max(hero.clientWidth * .4, 300));
      const y = clamp((pointerY - bounds.top - bounds.height / 2) / Math.max(hero.clientHeight * .45, 240));
      element.style.setProperty("--move-pitch", `${(-y * 7).toFixed(2)}deg`);
      element.style.setProperty("--move-yaw", `${(x * 10).toFixed(2)}deg`);
    };
    const move = (event: PointerEvent) => {
      if (!preference.matches || event.pointerType !== "mouse") return;
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(update);
    };
    // Pointer events drive the effect; there is no idle animation loop.
    hero.addEventListener("pointermove", move, { passive: true });
    hero.addEventListener("pointerleave", reset);
    window.addEventListener("scroll", reset, { passive: true });
    window.addEventListener("blur", reset);
    preference.addEventListener("change", reset);
    return () => {
      reset();
      hero.removeEventListener("pointermove", move);
      hero.removeEventListener("pointerleave", reset);
      window.removeEventListener("scroll", reset);
      window.removeEventListener("blur", reset);
      preference.removeEventListener("change", reset);
    };
  }, [reducedMotion, paused, staticMode]);

  return (
    <span className="hero-word-perspective" ref={word}>
      <em className="hero-move">
        {[6, 5, 4, 3, 2, 1].map((depth) => (
          <span
            className="hero-move-depth"
            aria-hidden="true"
            key={depth}
            style={{ "--depth": depth } as CSSProperties}
          >
            move.
          </span>
        ))}
        <span className="hero-move-face">move.</span>
      </em>
    </span>
  );
}
